import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRest } from "@/composables/index";

const emptyForm = () => ({
  tipoEfecto: "",
  categoria: "",
  subCategoria: "",
  tipoSubCategoria: "",
  marca: "",
  modelo: "",
  anio: "",
  nroChasis: "",
  nroMotor: "",
  dominio: "",
  nroSerie: "",
  imei: "",
  nroAbonado: "",
  color: "",
  ActuacionEfectoID: "",
  EfectoDepositoID: "",
});

export const useEffectStore = defineStore("effect", () => {
  const selected = ref(null);
  const form = ref(emptyForm());
  const formType = ref("view"); //estados del formulario view, edit, create
  const effectList = ref([]);
  const actuacionID = ref("");
  const rest = useRest();
  const isLoading = ref(false);
  const baseUrl = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/actuacion-efecto`;

  const fetchEfectos = async (id) => {
    actuacionID.value = id;
    try {
      const url = `${baseUrl}/?search={"ActuacionID":[{"operator":"=","value":"${actuacionID.value}"}]}`;
      const resp = await rest.get(url);
      effectList.value = resp.data.data;
      selectFirst();
    } catch (e) {
      console.error("Error al traer Efectos", e);
    }
  };

  const validateForm = () => {
    const camposObligatorios = ["tipoEfecto", "categoria", "subCategoria", "marca", "modelo"];

    if (!form.value) return false;

    const invalidField = camposObligatorios.find(
      (campo) => !form.value[campo] || form.value[campo] === ""
    );

    return !invalidField;
  };

  const handlePutEfectos = async () => {
    if (!validateForm()) return;
    isLoading.value = true;

    const formatedEffect = {
      Tipoefecto: form.value.tipoEfecto,
      EstadoEfectoActuacionID: form.value.tipoEfecto,
      EfectoDepositoID: null,
      CategoriaID: form.value.categoria,
      SubcategoriaID: form.value.subCategoria,
      TipoID: form.value.tipoSubCategoria,
      MarcaID: form.value.marca,
      ModeloID: form.value.modelo,
      Anio: form.value.anio === "Sin Registros" ? "" : form.value.anio,
      Chasis: form.value.nroChasis === "Sin Registros" ? "" : form.value.nroChasis,
      NumeroMotor: form.value.nroMotor === "Sin Registros" ? "" : form.value.nroMotor,
      Dominio: form.value.dominio === "Sin Registros" ? "" : form.value.dominio,
      NumeroSerie: form.value.nroSerie === "Sin Registros" ? "" : form.value.nroSerie,
      Imei: form.value.imei === "Sin Registros" ? "" : form.value.imei,
      Abonado: form.value.nroAbonado === "Sin Registros" ? "" : form.value.nroAbonado,
      Color: form.value.color === "Sin Registros" ? "" : form.value.color,
    };
    if (isEdit.value) {
      effectList.value = effectList.value.map((effect) =>
        effect.ActuacionEfectoID === selectedId.value ? { ...effect, ...formatedEffect } : effect
      );
    } else if (isCreate.value) {
      handleCreateEffect(formatedEffect);
    }
    await handleSave();
  };

  const handleDeleteEffect = (locally = false) => {
    isLoading.value = true;
    effectList.value = effectList.value.filter(
      (effect) => effect.ActuacionEfectoID !== selectedId.value
    );
    if (!locally) handleSave();
  };

  const handleCreateEffect = (newEffect) => {
    effectList.value = [
      ...effectList.value,
      {
        ...newEffect,
      },
    ];
  };

  const handleSave = async () => {
    //siempre pega al mismo endpoint, solo se modifica el arreglo original
    try {
      let options = {
        url: `${baseUrl}/${actuacionID.value}`,
        formData: { Efectos: effectList.value },
        ContentType: "text/plain",
      };
      await rest.put(options);
    } catch (error) {
      console.error("Error al editar efectos", error);
    } finally {
      isLoading.value = false;
      if (isCreate.value || isEdit.value) triggerSuccessModal();
      startView();
      clear();
      fetchEfectos(actuacionID.value);
    }
  };

  const select = (effect) => {
    selected.value = effect;
    hydrateFormFromDoc(effect);
    formType.value = "view";
  };

  const startCreate = () => {
    selected.value = null;
    form.value = emptyForm();
    formType.value = "create";
  };

  const hydrateFormFromDoc = (effect) => {
    form.value = {
      tipoEfecto: effect?.Tipoefecto || "",
      categoria: effect?.CategoriaID || "",
      subCategoria: effect?.SubcategoriaID || "",
      tipoSubCategoria: effect?.TipoID || "",
      marca: effect?.MarcaID || "",
      modelo: effect?.ModeloID || "",
      anio: effect?.Anio || "Sin Registros",
      nroChasis: effect?.Chasis || "",
      nroMotor: effect?.NumeroMotor || "Sin Registros",
      dominio: effect?.Dominio || "Sin Registros",
      nroSerie: effect?.NumeroSerie || "Sin Registros",
      imei: effect?.Imei || "Sin Registros",
      nroAbonado: effect?.Abonado || "Sin Registros",
      color: effect?.Color || "Sin Registros",
      ActuacionEfectoID: effect?.ActuacionEfectoID || "",
      EfectoDepositoID: effect?.EfectoDepositoID || "",
    };
  };

  const startEdit = () => {
    formType.value = "edit";
  };
  const startView = () => {
    formType.value = "view";
  };

  const resetForm = () => {
    form.value = emptyForm();
  };

  const clear = () => {
    selected.value = null;
    resetForm();
  };

  const selectFirst = () => {
    if (effectList.value?.length) {
      select(effectList.value[0]);
    }
  };

  const selectedId = computed(() => selected.value?.ActuacionEfectoID ?? null);

  const isSelected = (id) => selected.value?.ActuacionEfectoID === id;
  const isView = computed(() => formType.value === "view");
  const isEdit = computed(() => formType.value === "edit");
  const isCreate = computed(() => formType.value === "create");
  const inputsDisabled = computed(() => formType.value === "view");
  const isFormValid = computed(() => validateForm());

  const showSuccessModal = ref();

  const setShowSuccessModal = (fn) => {
    showSuccessModal.value = fn;
  };

  const triggerSuccessModal = () => {
    showSuccessModal.value?.();
  };

  const handleSaveLocally = async () => {
    if (!validateForm()) return;
    isLoading.value = true;
    const formatedEffect = {
      ActuacionEfectoID: isCreate.value ? `temp-${Date.now()}` : form.value.ActuacionEfectoID,
      Tipoefecto: form.value.tipoEfecto,
      EstadoEfectoActuacionID: form.value.tipoEfecto,
      EfectoDepositoID: null,
      CategoriaID: form.value.categoria,
      SubcategoriaID: form.value.subCategoria,
      TipoID: form.value.tipoSubCategoria,
      MarcaID: form.value.marca,
      ModeloID: form.value.modelo,
      Anio: form.value.anio === "Sin Registros" ? "" : form.value.anio,
      Chasis: form.value.nroChasis === "Sin Registros" ? "" : form.value.nroChasis,
      NumeroMotor: form.value.nroMotor === "Sin Registros" ? "" : form.value.nroMotor,
      Dominio: form.value.dominio === "Sin Registros" ? "" : form.value.dominio,
      NumeroSerie: form.value.nroSerie === "Sin Registros" ? "" : form.value.nroSerie,
      Imei: form.value.imei === "Sin Registros" ? "" : form.value.imei,
      Abonado: form.value.nroAbonado === "Sin Registros" ? "" : form.value.nroAbonado,
      Color: form.value.color === "Sin Registros" ? "" : form.value.color,
    };

    if (isCreate.value) {
      handleCreateEffect(formatedEffect);
    } else if (isEdit.value) {
      effectList.value = effectList.value.map((effect) =>
        effect.ActuacionEfectoID === selectedId.value ? { ...effect, ...formatedEffect } : effect
      );
    }
    isLoading.value = false;
    startView();
    clear();
    if (effectList.value.length > 0) {
      selectFirst();
    }
  };

  return {
    // state
    selected,
    form,
    formType,
    effectList,
    isLoading,

    // actions
    select,
    hydrateFormFromDoc,
    resetForm,
    clear,
    startCreate,
    startEdit,
    handleSave,
    fetchEfectos,
    handleCreateEffect,
    handleDeleteEffect,
    handlePutEfectos,
    startView,
    setShowSuccessModal,
    triggerSuccessModal,
    selectFirst,
    handleSaveLocally,
    validateForm,

    // getters
    selectedId,
    isSelected,
    isCreate,
    isEdit,
    isView,
    inputsDisabled,
    isFormValid,
  };
});
